import React, { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Rocket, Building2, ArrowRight, ArrowLeft, Check, 
  Sparkles, Clock, AlertTriangle, Globe2, Globe,
  DollarSign, Users, Tag, Lightbulb,
  Star, Shield, Loader2, GitCompare, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS - EXACT SAME OPTIONS AS ORIGINAL FORM (LandingPage.js)
// These are defined OUTSIDE the component to prevent re-creation on renders
// ═══════════════════════════════════════════════════════════════════════════════

const MONETIZATION_OPTIONS = [
  { value: "One-time Purchase", label: "One-time Purchase" },
  { value: "Subscription (B2C)", label: "Subscription (B2C)" },
  { value: "Subscription (B2B/Enterprise)", label: "Subscription (B2B/Enterprise)" },
  { value: "Freemium", label: "Freemium" },
  { value: "Transaction/Commission-based", label: "Transaction/Commission-based" },
  { value: "Ad-supported/Free", label: "Ad-supported/Free" }
];

const INDUSTRIES = [
  "Technology & Software",
  "E-commerce & Retail",
  "Finance & Banking",
  "Healthcare & Pharma",
  "Food & Beverage",
  "Fashion & Apparel",
  "Beauty & Cosmetics",
  "Travel & Hospitality",
  "Real Estate & Property",
  "Education & EdTech",
  "Media & Entertainment",
  "Automotive",
  "Manufacturing",
  "Agriculture",
  "Energy & Utilities",
  "Logistics & Supply Chain",
  "Professional Services",
  "Non-Profit & NGO",
  "Sports & Fitness",
  "Home & Living",
  "Pet Care",
  "Kids & Baby",
  "Jewelry & Accessories",
  "Art & Crafts",
  "Gaming",
  "Telecom",
  "Insurance",
  "Legal Services",
  "HR & Recruitment",
  "Marketing & Advertising",
  "Other"
];

const PRODUCT_TYPES = [
  { value: "Physical", label: "Physical Product" },
  { value: "Digital", label: "Digital Product/App" },
  { value: "Service", label: "Service" },
  { value: "Hybrid", label: "Hybrid (Product + Service)" }
];

const POSITIONING_OPTIONS = [
  { value: "Budget", label: "Budget" },
  { value: "Mid-Range", label: "Mid-Range" },
  { value: "Premium", label: "Premium" },
  { value: "Luxury", label: "Luxury" },
  { value: "Mass", label: "Mass Market" },
  { value: "Ultra-Premium", label: "Ultra-Premium" }
];

const MARKET_SCOPE_OPTIONS = [
  { value: "Single Country", label: "Single Country" },
  { value: "Multi-Country", label: "Multi-Country" },
  { value: "Global", label: "Global" }
];

const STEP_LABELS_NEW = ['Brand Type', 'Basic Info', 'Context', 'Enhance'];
const STEP_LABELS_EXISTING = ['Brand Type', 'Digital Presence', 'Basic Info', 'Context', 'Enhance'];

// ═══════════════════════════════════════════════════════════════════════════════
// BRAND WIZARD - Multi-Step Questionnaire
// ═══════════════════════════════════════════════════════════════════════════════

// Platform options for existing brand digital presence
const PLATFORM_OPTIONS = [
  { id: 'google_business', label: 'Google Business', icon: '🔍' },
  { id: 'facebook', label: 'Facebook', icon: '📘' },
  { id: 'instagram', label: 'Instagram', icon: '📸' },
  { id: 'twitter', label: 'Twitter/X', icon: '🐦' },
  { id: 'linkedin', label: 'LinkedIn', icon: '💼' },
  { id: 'youtube', label: 'YouTube', icon: '▶️' },
  { id: 'tiktok', label: 'TikTok', icon: '🎵' },
  { id: 'pinterest', label: 'Pinterest', icon: '📌' },
  { id: 'threads', label: 'Threads', icon: '🧵' },
];

const BrandWizard = ({ onComplete, onStartProcessing }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Form state - using individual state hooks for better performance
  const [brandStatus, setBrandStatus] = useState(null);
  const [brandAge, setBrandAge] = useState(null);
  const [brandNames, setBrandNames] = useState('');
  const [industry, setIndustry] = useState('');
  const [category, setCategory] = useState('');
  const [evaluationMode, setEvaluationMode] = useState('single');
  const [nameOrigin, setNameOrigin] = useState('');
  const [usp, setUsp] = useState('');
  const [productType, setProductType] = useState('Digital');
  const [monetizationModel, setMonetizationModel] = useState('');
  const [positioning, setPositioning] = useState('Premium');
  const [marketScope, setMarketScope] = useState('Multi-Country');
  const [countries, setCountries] = useState('');
  const [knownCompetitors, setKnownCompetitors] = useState('');
  const [productKeywords, setProductKeywords] = useState('');
  
  // NEW: Existing brand digital presence state
  const [ownedWebsites, setOwnedWebsites] = useState(['']);
  const [ownedPlatforms, setOwnedPlatforms] = useState([]);

  // Dynamic step count - 5 steps for existing brands, 4 for new brands
  const totalSteps = brandStatus === 'existing' ? 5 : 4;
  
  // Get actual step number accounting for the extra "Digital Presence" step
  const getActualStep = (step) => {
    if (brandStatus === 'existing' && step > 1) {
      return step; // Steps 1, 2 (digital presence), 3, 4, 5
    }
    return step; // Steps 1, 2, 3, 4
  };

  // Memoized brand count
  const brandCount = useMemo(() => {
    return brandNames.split(',').map(n => n.trim()).filter(n => n).length;
  }, [brandNames]);

  // Step validation - memoized
  // For existing brands: 1=Brand Type, 2=Digital Presence, 3=Basic Info, 4=Context, 5=Enhance
  // For new brands: 1=Brand Type, 2=Basic Info, 3=Context, 4=Enhance
  const isStepValid = useMemo(() => {
    if (brandStatus === 'existing') {
      // Existing brand flow (5 steps)
      switch (currentStep) {
        case 1:
          return brandAge === '0-1';
        case 2:
          // Digital presence step - always valid (optional fields)
          return true;
        case 3:
          return brandNames.trim() && industry && category.trim();
        case 4:
          return nameOrigin.trim() && usp.trim() && monetizationModel;
        case 5:
          return true;
        default:
          return false;
      }
    } else {
      // New brand flow (4 steps)
      switch (currentStep) {
        case 1:
          return brandStatus === 'new';
        case 2:
          return brandNames.trim() && industry && category.trim();
        case 3:
          return nameOrigin.trim() && usp.trim() && monetizationModel;
        case 4:
          return true;
        default:
          return false;
      }
    }
  }, [currentStep, brandStatus, brandAge, brandNames, industry, category, nameOrigin, usp, monetizationModel]);

  // Navigation handlers
  const nextStep = useCallback(() => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      
      // Trigger SSOT processing at the right step
      const basicInfoStep = brandStatus === 'existing' ? 3 : 2;
      if (currentStep === basicInfoStep && onStartProcessing) {
        onStartProcessing('ssot', { brand_names: brandNames, industry, category });
      }
    }
  }, [currentStep, totalSteps, brandNames, industry, category, onStartProcessing, brandStatus]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  // Helper functions for owned websites management
  const addWebsiteField = useCallback(() => {
    setOwnedWebsites(prev => [...prev, '']);
  }, []);

  const removeWebsiteField = useCallback((index) => {
    setOwnedWebsites(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateWebsite = useCallback((index, value) => {
    setOwnedWebsites(prev => prev.map((w, i) => i === index ? value : w));
  }, []);

  const togglePlatform = useCallback((platformId) => {
    setOwnedPlatforms(prev => 
      prev.includes(platformId) 
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  }, []);

  // Submit handler
  const handleSubmit = useCallback(() => {
    setIsProcessing(true);
    
    const brandNamesList = brandNames.split(',').map(n => n.trim()).filter(n => n);
    const countriesList = countries ? countries.split(',').map(c => c.trim()).filter(c => c) : ['India'];
    
    // Filter out empty website entries
    const validWebsites = ownedWebsites.filter(w => w.trim());
    
    const payload = {
      brand_names: brandNamesList,
      industry: industry || 'General',
      category: category || 'General',
      product_type: productType || 'Digital',
      usp: usp || '',
      name_origin: nameOrigin || '',
      monetization_model: monetizationModel || null,
      positioning: positioning || 'Premium',
      market_scope: marketScope || 'Multi-Country',
      countries: countriesList,
      known_competitors: knownCompetitors ? knownCompetitors.split(',').map(c => c.trim()).filter(c => c) : [],
      product_keywords: productKeywords ? productKeywords.split(',').map(k => k.trim()).filter(k => k) : [],
      problem_statement: '',
      evaluation_mode: evaluationMode,
      brand_ownership: brandStatus === 'existing' ? '0-1' : 'new',
      // NEW: Digital presence data for existing brands
      owned_websites: validWebsites,
      owned_platforms: ownedPlatforms
    };
    
    onComplete(payload);
  }, [brandNames, countries, industry, category, productType, usp, nameOrigin, monetizationModel, positioning, marketScope, knownCompetitors, productKeywords, evaluationMode, brandStatus, onComplete, ownedWebsites, ownedPlatforms]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER STEP CONTENT - Inline to prevent re-mounting
  // ═══════════════════════════════════════════════════════════════════════════════

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-black text-slate-800 mb-2">Let&apos;s get started</h2>
              <p className="text-slate-500">Tell us about your brand journey</p>
            </div>

            <div className="space-y-4">
              {/* NEW BRAND Card */}
              <motion.div
                onClick={() => setBrandStatus('new')}
                className={`relative p-6 rounded-2xl cursor-pointer transition-all overflow-hidden ${
                  brandStatus === 'new'
                    ? 'bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 text-white shadow-2xl shadow-violet-500/30'
                    : 'bg-white border-2 border-slate-200 hover:border-violet-300 hover:shadow-lg'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {brandStatus === 'new' && (
                  <motion.div 
                    className="absolute top-4 right-4 w-6 h-6 bg-white rounded-full flex items-center justify-center"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                  >
                    <Check className="w-4 h-4 text-violet-600" />
                  </motion.div>
                )}
                
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                    brandStatus === 'new' ? 'bg-white/20' : 'bg-violet-100'
                  }`}>
                    <Rocket className={`w-7 h-7 ${brandStatus === 'new' ? 'text-white' : 'text-violet-600'}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-xl font-black mb-1 ${brandStatus === 'new' ? 'text-white' : 'text-slate-800'}`}>
                      New Brand
                    </h3>
                    <p className={`text-sm ${brandStatus === 'new' ? 'text-white/80' : 'text-slate-500'}`}>
                      I&apos;m considering this name for a new venture
                    </p>
                  </div>
                </div>
                
                {brandStatus === 'new' && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-4 bg-white/10 rounded-xl"
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      <span className="text-sm font-medium">Perfect! Let&apos;s evaluate your brand name(s)</span>
                    </div>
                  </motion.div>
                )}
              </motion.div>

              {/* EXISTING BRAND Card */}
              <motion.div
                onClick={() => setBrandStatus('existing')}
                className={`relative p-6 rounded-2xl cursor-pointer transition-all overflow-hidden ${
                  brandStatus === 'existing'
                    ? 'bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 text-white shadow-2xl'
                    : 'bg-white border-2 border-slate-200 hover:border-slate-300 hover:shadow-lg'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {brandStatus === 'existing' && (
                  <motion.div 
                    className="absolute top-4 right-4 w-6 h-6 bg-white rounded-full flex items-center justify-center"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                  >
                    <Check className="w-4 h-4 text-slate-800" />
                  </motion.div>
                )}
                
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                    brandStatus === 'existing' ? 'bg-white/20' : 'bg-slate-100'
                  }`}>
                    <Building2 className={`w-7 h-7 ${brandStatus === 'existing' ? 'text-white' : 'text-slate-600'}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-xl font-black mb-1 ${brandStatus === 'existing' ? 'text-white' : 'text-slate-800'}`}>
                      Existing Brand
                    </h3>
                    <p className={`text-sm ${brandStatus === 'existing' ? 'text-white/80' : 'text-slate-500'}`}>
                      I already own this brand
                    </p>
                  </div>
                </div>

                {/* Sub-options for existing brand */}
                {brandStatus === 'existing' && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 space-y-3"
                  >
                    <p className="text-sm text-white/60 mb-3">How old is your brand?</p>
                    
                    <motion.div
                      onClick={(e) => { e.stopPropagation(); setBrandAge('0-1'); }}
                      className={`p-4 rounded-xl cursor-pointer transition-all ${
                        brandAge === '0-1'
                          ? 'bg-emerald-500/30 border border-emerald-400/50'
                          : 'bg-white/10 hover:bg-white/20'
                      }`}
                      whileHover={{ scale: 1.01 }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Clock className="w-5 h-5" />
                          <div>
                            <span className="font-semibold">Less than 1 year</span>
                            <p className="text-xs text-white/60">Haven&apos;t filed trademark yet</p>
                          </div>
                        </div>
                        {brandAge === '0-1' && (
                          <div className="w-5 h-5 bg-emerald-400 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </motion.div>

                    <motion.div
                      onClick={(e) => { e.stopPropagation(); setBrandAge('1+'); }}
                      className={`p-4 rounded-xl cursor-pointer transition-all ${
                        brandAge === '1+'
                          ? 'bg-amber-500/30 border border-amber-400/50'
                          : 'bg-white/10 hover:bg-white/20'
                      }`}
                      whileHover={{ scale: 1.01 }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Shield className="w-5 h-5" />
                          <div>
                            <span className="font-semibold">1+ years</span>
                            <p className="text-xs text-white/60">Established brand</p>
                          </div>
                        </div>
                        {brandAge === '1+' && (
                          <div className="w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </motion.div>

              {/* Not For You Message */}
              {brandStatus === 'existing' && brandAge === '1+' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-black text-amber-800 text-lg mb-2">This product is not for you</h4>
                      <p className="text-amber-700 text-sm mb-2">
                        <strong>RIGHTNAME.AI</strong> is designed for <strong>pre-seed founders</strong> deciding on a brand name <strong>before filing trademark</strong>.
                      </p>
                      <p className="text-amber-600 text-sm">
                        Since you have an established brand (1+ years), our evaluation tools won&apos;t be relevant for your needs.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Continue Message for 0-1 years */}
              {brandStatus === 'existing' && brandAge === '0-1' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border-2 border-emerald-200"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Check className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-black text-emerald-800 text-lg mb-2">Great! Let&apos;s help you file your trademark</h4>
                      <p className="text-emerald-700 text-sm">
                        We&apos;ll analyze your brand name and provide insights to help you protect it legally. You can also compare alternative names if you have them.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        );

      case 2:
        // For existing brands, show Digital Presence step
        // For new brands, show Basic Information
        if (brandStatus === 'existing') {
          return (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-black text-slate-800 mb-2">Your Digital Presence</h2>
                <p className="text-slate-500">Help us avoid flagging your own brand as a conflict</p>
              </div>

              {/* Explanation Card */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-800 font-medium">Why we ask this</p>
                    <p className="text-xs text-blue-600 mt-1">
                      When we search for trademark conflicts, we might find YOUR OWN websites and social profiles. 
                      By telling us what you own, we&apos;ll exclude them from conflict detection and show them as &quot;Your Existing Presence&quot; instead.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                {/* Owned Websites */}
                <div className="space-y-3">
                  <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <Globe2 className="w-4 h-4 text-violet-500" />
                    Website(s) you own
                    <span className="text-xs font-normal text-slate-400">(optional)</span>
                  </Label>
                  
                  {ownedWebsites.map((website, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={website}
                        onChange={(e) => updateWebsite(index, e.target.value)}
                        placeholder="e.g., rightname.ai, mybrand.com"
                        className="flex-1 h-12 rounded-xl border-slate-200 focus:border-violet-500 focus:ring-violet-500"
                      />
                      {ownedWebsites.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeWebsiteField(index)}
                          className="h-12 w-12 rounded-xl border-red-200 text-red-500 hover:bg-red-50"
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  ))}
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addWebsiteField}
                    className="w-full h-10 rounded-xl border-dashed border-slate-300 text-slate-500 hover:border-violet-400 hover:text-violet-600"
                  >
                    + Add another website
                  </Button>
                </div>

                {/* Owned Platforms */}
                <div className="space-y-3">
                  <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <Users className="w-4 h-4 text-violet-500" />
                    Platforms where you&apos;re registered
                    <span className="text-xs font-normal text-slate-400">(optional)</span>
                  </Label>
                  
                  <div className="grid grid-cols-3 gap-2">
                    {PLATFORM_OPTIONS.map((platform) => (
                      <motion.div
                        key={platform.id}
                        onClick={() => togglePlatform(platform.id)}
                        className={`p-3 rounded-xl cursor-pointer transition-all flex items-center gap-2 ${
                          ownedPlatforms.includes(platform.id)
                            ? 'bg-violet-100 border-2 border-violet-500'
                            : 'bg-slate-50 border-2 border-slate-200 hover:border-violet-300'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span className="text-lg">{platform.icon}</span>
                        <span className={`text-sm font-medium ${
                          ownedPlatforms.includes(platform.id) ? 'text-violet-700' : 'text-slate-600'
                        }`}>
                          {platform.label}
                        </span>
                        {ownedPlatforms.includes(platform.id) && (
                          <Check className="w-4 h-4 text-violet-600 ml-auto" />
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                {(ownedWebsites.some(w => w.trim()) || ownedPlatforms.length > 0) && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-emerald-50 rounded-xl border border-emerald-200"
                  >
                    <p className="text-sm text-emerald-800 font-medium mb-2">We&apos;ll exclude from conflicts:</p>
                    <div className="flex flex-wrap gap-2">
                      {ownedWebsites.filter(w => w.trim()).map((website, index) => (
                        <Badge key={`web-${index}`} variant="secondary" className="bg-emerald-100 text-emerald-700">
                          🌐 {website}
                        </Badge>
                      ))}
                      {ownedPlatforms.map((platformId) => {
                        const platform = PLATFORM_OPTIONS.find(p => p.id === platformId);
                        return platform ? (
                          <Badge key={platformId} variant="secondary" className="bg-emerald-100 text-emerald-700">
                            {platform.icon} {platform.label}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </motion.div>
                )}

                {/* Skip message */}
                {!ownedWebsites.some(w => w.trim()) && ownedPlatforms.length === 0 && (
                  <p className="text-center text-sm text-slate-400">
                    You can skip this step if you don&apos;t have any existing digital presence yet.
                  </p>
                )}
              </div>
            </div>
          );
        }
        
        // For NEW brands, show Basic Information (original case 2)
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-black text-slate-800 mb-2">Basic Information</h2>
              <p className="text-slate-500">Tell us about your brand</p>
            </div>

            <div className="space-y-5">
              {/* Evaluation Mode Toggle */}
              <div className="space-y-2">
                <Label className="text-sm font-bold text-slate-700">Evaluation Mode</Label>
                <div className="grid grid-cols-2 gap-3">
                  <motion.div
                    onClick={() => setEvaluationMode('single')}
                    className={`p-4 rounded-xl cursor-pointer transition-all flex items-center gap-3 ${
                      evaluationMode === 'single'
                        ? 'bg-violet-100 border-2 border-violet-500'
                        : 'bg-slate-50 border-2 border-slate-200 hover:border-violet-300'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FileText className={`w-5 h-5 ${evaluationMode === 'single' ? 'text-violet-600' : 'text-slate-400'}`} />
                    <div>
                      <p className={`font-semibold ${evaluationMode === 'single' ? 'text-violet-700' : 'text-slate-700'}`}>
                        Single Name
                      </p>
                      <p className="text-xs text-slate-500">Deep analysis</p>
                    </div>
                  </motion.div>
                  
                  <motion.div
                    onClick={() => setEvaluationMode('compare')}
                    className={`p-4 rounded-xl cursor-pointer transition-all flex items-center gap-3 ${
                      evaluationMode === 'compare'
                        ? 'bg-fuchsia-100 border-2 border-fuchsia-500'
                        : 'bg-slate-50 border-2 border-slate-200 hover:border-fuchsia-300'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <GitCompare className={`w-5 h-5 ${evaluationMode === 'compare' ? 'text-fuchsia-600' : 'text-slate-400'}`} />
                    <div>
                      <p className={`font-semibold ${evaluationMode === 'compare' ? 'text-fuchsia-700' : 'text-slate-700'}`}>
                        Compare Names
                      </p>
                      <p className="text-xs text-slate-500">Up to 5 names</p>
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Brand Name(s) Input */}
              <div className="space-y-2">
                <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-500" />
                  {evaluationMode === 'single' ? 'Brand Name' : 'Brand Names (comma-separated)'} 
                  <span className="text-red-500">*</span>
                  {brandCount > 0 && (
                    <Badge variant="outline" className="ml-2 text-xs">
                      {brandCount} name{brandCount > 1 ? 's' : ''}
                    </Badge>
                  )}
                </Label>
                <Input
                  data-testid="brand-names-input"
                  value={brandNames}
                  onChange={(e) => setBrandNames(e.target.value)}
                  placeholder={evaluationMode === 'single' 
                    ? "e.g., RIGHTNAME" 
                    : "e.g., RIGHTNAME, BrandGenius, NameCraft"
                  }
                  className="h-14 text-lg bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-violet-500 focus:ring-violet-500/20 transition-all"
                />
                <p className="text-xs text-slate-400">
                  {evaluationMode === 'single' 
                    ? "Enter the brand name you want to evaluate"
                    : "Enter up to 5 brand names separated by commas to compare"
                  }
                </p>
              </div>

              {/* Industry Select */}
              <div className="space-y-2">
                <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-violet-500" />
                  Industry <span className="text-red-500">*</span>
                </Label>
                <Select onValueChange={setIndustry} value={industry}>
                  <SelectTrigger className="h-14 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium hover:border-violet-300 transition-all">
                    <SelectValue placeholder="Select your industry..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {INDUSTRIES.map((ind) => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Category Input */}
              <div className="space-y-2">
                <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-violet-500" />
                  Category <span className="text-red-500">*</span>
                </Label>
                <Input
                  data-testid="category-input"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g., Healthy Ice Cream, Food Delivery, EdTech"
                  className="h-14 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-violet-500 transition-all"
                />
                <p className="text-xs text-slate-400">Be specific - &quot;Healthy Ice Cream&quot; is better than just &quot;Food&quot;</p>
              </div>
            </div>
          </div>
        );

      case 3:
        // For existing brands: Basic Information
        // For new brands: Brand Context
        if (brandStatus === 'existing') {
          // Basic Information for existing brands
          return (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-black text-slate-800 mb-2">Basic Information</h2>
                <p className="text-slate-500">Tell us about your brand</p>
              </div>

              <div className="space-y-5">
                {/* Evaluation Mode Toggle */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-slate-700">Evaluation Mode</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <motion.div
                      onClick={() => setEvaluationMode('single')}
                      className={`p-4 rounded-xl cursor-pointer transition-all flex items-center gap-3 ${
                        evaluationMode === 'single'
                          ? 'bg-violet-100 border-2 border-violet-500'
                          : 'bg-slate-50 border-2 border-slate-200 hover:border-violet-300'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <FileText className={`w-5 h-5 ${evaluationMode === 'single' ? 'text-violet-600' : 'text-slate-400'}`} />
                      <div>
                        <p className={`font-semibold ${evaluationMode === 'single' ? 'text-violet-700' : 'text-slate-700'}`}>
                          Single Name
                        </p>
                        <p className="text-xs text-slate-500">Deep analysis</p>
                      </div>
                    </motion.div>

                    <motion.div
                      onClick={() => setEvaluationMode('compare')}
                      className={`p-4 rounded-xl cursor-pointer transition-all flex items-center gap-3 ${
                        evaluationMode === 'compare'
                          ? 'bg-fuchsia-100 border-2 border-fuchsia-500'
                          : 'bg-slate-50 border-2 border-slate-200 hover:border-fuchsia-300'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <GitCompare className={`w-5 h-5 ${evaluationMode === 'compare' ? 'text-fuchsia-600' : 'text-slate-400'}`} />
                      <div>
                        <p className={`font-semibold ${evaluationMode === 'compare' ? 'text-fuchsia-700' : 'text-slate-700'}`}>
                          Compare Names
                        </p>
                        <p className="text-xs text-slate-500">Up to 5 names</p>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Brand Name Input */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-violet-500" />
                    Brand Name {evaluationMode === 'compare' ? '(comma-separated)' : ''} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    data-testid="brand-name-input"
                    value={brandNames}
                    onChange={(e) => setBrandNames(e.target.value)}
                    placeholder={evaluationMode === 'compare' ? "e.g., RIGHTNAME, NameCheck, BrandValid" : "e.g., RIGHTNAME"}
                    className="h-14 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-violet-500 transition-all"
                  />
                  <p className="text-xs text-slate-400">
                    {evaluationMode === 'compare' ? `Enter ${brandCount}/5 names` : 'Enter the brand name you want to evaluate'}
                  </p>
                </div>

                {/* Industry Dropdown */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-violet-500" />
                    Industry <span className="text-red-500">*</span>
                  </Label>
                  <Select onValueChange={setIndustry} value={industry}>
                    <SelectTrigger className="h-14 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium hover:border-violet-300 transition-all">
                      <SelectValue placeholder="Select your industry..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {INDUSTRIES.map((ind) => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Category Input */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <Tag className="w-4 h-4 text-violet-500" />
                    Category <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    data-testid="category-input"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="e.g., Healthy Ice Cream, Food Delivery, EdTech"
                    className="h-14 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-violet-500 transition-all"
                  />
                  <p className="text-xs text-slate-400">Be specific - &quot;Healthy Ice Cream&quot; is better than just &quot;Food&quot;</p>
                </div>
              </div>
            </div>
          );
        }
        
        // Brand Context for new brands (original case 3)
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-black text-slate-800 mb-2">Brand Context</h2>
              <p className="text-slate-500">Help us understand your vision</p>
            </div>

            <div className="space-y-5">
              {/* Why This Name */}
              <div className="space-y-2">
                <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  Why This Name? <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  data-testid="name-origin-input"
                  value={nameOrigin}
                  onChange={(e) => setNameOrigin(e.target.value)}
                  placeholder="e.g., 'ELVN = Eleven, represents 110 calories & lucky number 11:11' or 'Family name' or 'Coined word - no meaning'"
                  className="min-h-[100px] bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-violet-500 transition-all resize-none"
                />
                <p className="text-xs text-slate-400">Origin, meaning, or inspiration behind your brand name</p>
              </div>

              {/* USP */}
              <div className="space-y-2">
                <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-red-500" />
                  What&apos;s Your USP? <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  data-testid="usp-input"
                  value={usp}
                  onChange={(e) => setUsp(e.target.value)}
                  placeholder="e.g., 'Low calorie, high protein ice cream for diabetics' or 'Fastest delivery in 10 mins'"
                  className="min-h-[100px] bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-violet-500 transition-all resize-none"
                />
                <p className="text-xs text-slate-400">What makes you DIFFERENT from competitors?</p>
              </div>

              {/* Product Type & Positioning */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-slate-700">Product Type</Label>
                  <Select onValueChange={setProductType} value={productType}>
                    <SelectTrigger className="h-12 bg-slate-50 border-2 border-slate-200 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRODUCT_TYPES.map((pt) => (
                        <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-bold text-slate-700">Positioning</Label>
                  <Select onValueChange={setPositioning} value={positioning}>
                    <SelectTrigger className="h-12 bg-slate-50 border-2 border-slate-200 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {POSITIONING_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Monetization Model */}
              <div className="space-y-2">
                <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-500" />
                  Monetization Model <span className="text-red-500">*</span>
                </Label>
                <Select onValueChange={setMonetizationModel} value={monetizationModel}>
                  <SelectTrigger className="h-12 bg-slate-50 border-2 border-slate-200 rounded-xl">
                    <SelectValue placeholder="How will you make money?" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONETIZATION_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Market Scope & Countries */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-slate-700">Market Scope</Label>
                  <Select onValueChange={setMarketScope} value={marketScope}>
                    <SelectTrigger className="h-12 bg-slate-50 border-2 border-slate-200 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MARKET_SCOPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <Globe2 className="w-4 h-4 text-blue-500" />
                    Countries
                  </Label>
                  <Input
                    data-testid="countries-input"
                    value={countries}
                    onChange={(e) => setCountries(e.target.value)}
                    placeholder="e.g., India, USA, UK"
                    className="h-12 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-violet-500 transition-all"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        // For existing brands: Brand Context
        // For new brands: Enhanced Analysis
        if (brandStatus === 'existing') {
          // Brand Context for existing brands
          return (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-black text-slate-800 mb-2">Brand Context</h2>
                <p className="text-slate-500">Help us understand your vision</p>
              </div>

              <div className="space-y-5">
                {/* Why This Name */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-500" />
                    Why This Name? <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    data-testid="name-origin-input"
                    value={nameOrigin}
                    onChange={(e) => setNameOrigin(e.target.value)}
                    placeholder="e.g., 'ELVN = Eleven, represents 110 calories & lucky number 11:11' or 'Family name' or 'Coined word - no meaning'"
                    className="min-h-[100px] bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-violet-500 transition-all resize-none"
                  />
                  <p className="text-xs text-slate-400">Origin, meaning, or inspiration behind your brand name</p>
                </div>

                {/* USP */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-red-500" />
                    What&apos;s Your USP? <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    data-testid="usp-input"
                    value={usp}
                    onChange={(e) => setUsp(e.target.value)}
                    placeholder="e.g., 'Low calorie, high protein ice cream for diabetics' or 'Fastest delivery in 10 mins'"
                    className="min-h-[100px] bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-violet-500 transition-all resize-none"
                  />
                  <p className="text-xs text-slate-400">What makes you DIFFERENT from competitors?</p>
                </div>

                {/* Product Type & Positioning */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-slate-700">Product Type</Label>
                    <Select onValueChange={setProductType} value={productType}>
                      <SelectTrigger className="h-12 bg-slate-50 border-2 border-slate-200 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Digital">Digital Product/App</SelectItem>
                        <SelectItem value="Physical">Physical Product</SelectItem>
                        <SelectItem value="Service">Service</SelectItem>
                        <SelectItem value="Hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-slate-700">Positioning</Label>
                    <Select onValueChange={setPositioning} value={positioning}>
                      <SelectTrigger className="h-12 bg-slate-50 border-2 border-slate-200 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Budget">Budget</SelectItem>
                        <SelectItem value="Mid-Range">Mid-Range</SelectItem>
                        <SelectItem value="Premium">Premium</SelectItem>
                        <SelectItem value="Luxury">Luxury</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Monetization Model */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-500" />
                    Monetization Model <span className="text-red-500">*</span>
                  </Label>
                  <Select onValueChange={setMonetizationModel} value={monetizationModel}>
                    <SelectTrigger className="h-12 bg-slate-50 border-2 border-slate-200 rounded-xl">
                      <SelectValue placeholder="How will you make money?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Subscription">Subscription/SaaS</SelectItem>
                      <SelectItem value="Transaction">Transaction Fee</SelectItem>
                      <SelectItem value="E-commerce">E-commerce/Product Sales</SelectItem>
                      <SelectItem value="Advertising">Advertising/Freemium</SelectItem>
                      <SelectItem value="Service">Service Fee</SelectItem>
                      <SelectItem value="Marketplace">Marketplace Commission</SelectItem>
                      <SelectItem value="Licensing">Licensing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Market Scope & Countries */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-slate-700">Market Scope</Label>
                    <Select onValueChange={setMarketScope} value={marketScope}>
                      <SelectTrigger className="h-12 bg-slate-50 border-2 border-slate-200 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Single Country">Single Country</SelectItem>
                        <SelectItem value="Multi-Country">Multi-Country</SelectItem>
                        <SelectItem value="Global">Global</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-500" />
                      Countries
                    </Label>
                    <Input
                      value={countries}
                      onChange={(e) => setCountries(e.target.value)}
                      placeholder="e.g., India, USA, UK"
                      className="h-12 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-violet-500 transition-all"
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        }
        
        // Enhanced Analysis for new brands (original case 4)
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full text-violet-700 text-sm font-medium mb-4">
                <Star className="w-4 h-4" />
                Optional - Enhance Accuracy
              </div>
              <h2 className="text-3xl font-black text-slate-800 mb-2">Enhanced Analysis</h2>
              <p className="text-slate-500">Help us find more accurate competitors</p>
            </div>

            <div className="space-y-5">
              {/* Known Competitors */}
              <div className="space-y-2">
                <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <Users className="w-4 h-4 text-orange-500" />
                  Known Competitors
                </Label>
                <Input
                  data-testid="competitors-input"
                  value={knownCompetitors}
                  onChange={(e) => setKnownCompetitors(e.target.value)}
                  placeholder="e.g., PhonePe, Paytm, GPay"
                  className="h-14 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-violet-500 transition-all"
                />
                <p className="text-xs text-slate-400">Top 3-5 competitors in your market (comma-separated)</p>
              </div>

              {/* Product Keywords */}
              <div className="space-y-2">
                <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-blue-500" />
                  Product Keywords
                </Label>
                <Input
                  data-testid="keywords-input"
                  value={productKeywords}
                  onChange={(e) => setProductKeywords(e.target.value)}
                  placeholder="e.g., UPI, wallet, payments, fintech"
                  className="h-14 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-violet-500 transition-all"
                />
                <p className="text-xs text-slate-400">Key terms describing your product (comma-separated)</p>
              </div>
            </div>

            {/* Benefits */}
            <div className="p-4 bg-gradient-to-r from-violet-50 to-fuchsia-50 rounded-xl border border-violet-200">
              <h4 className="font-bold text-violet-800 mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Why fill this?
              </h4>
              <ul className="text-sm text-violet-700 space-y-1">
                <li>• More accurate competitive landscape analysis</li>
                <li>• Better trademark conflict detection</li>
                <li>• Relevant market positioning insights</li>
              </ul>
            </div>
          </div>
        );

      case 5:
        // Enhanced Analysis for existing brands only
        if (brandStatus === 'existing') {
          return (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full text-violet-700 text-sm font-medium mb-4">
                  <Star className="w-4 h-4" />
                  Optional - Enhance Accuracy
                </div>
                <h2 className="text-3xl font-black text-slate-800 mb-2">Enhanced Analysis</h2>
                <p className="text-slate-500">Help us find more accurate competitors</p>
              </div>

              <div className="space-y-5">
                {/* Known Competitors */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <Users className="w-4 h-4 text-orange-500" />
                    Known Competitors
                  </Label>
                  <Input
                    data-testid="competitors-input"
                    value={knownCompetitors}
                    onChange={(e) => setKnownCompetitors(e.target.value)}
                    placeholder="e.g., PhonePe, Paytm, GPay"
                    className="h-14 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-violet-500 transition-all"
                  />
                  <p className="text-xs text-slate-400">Top 3-5 competitors in your market (comma-separated)</p>
                </div>

                {/* Product Keywords */}
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <Tag className="w-4 h-4 text-blue-500" />
                    Product Keywords
                  </Label>
                  <Input
                    data-testid="keywords-input"
                    value={productKeywords}
                    onChange={(e) => setProductKeywords(e.target.value)}
                    placeholder="e.g., UPI, wallet, payments, fintech"
                    className="h-14 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-violet-500 transition-all"
                  />
                  <p className="text-xs text-slate-400">Key terms describing your product (comma-separated)</p>
                </div>
              </div>

              {/* Benefits */}
              <div className="p-4 bg-gradient-to-r from-violet-50 to-fuchsia-50 rounded-xl border border-violet-200">
                <h4 className="font-bold text-violet-800 mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Why fill this?
                </h4>
                <ul className="text-sm text-violet-700 space-y-1">
                  <li>• More accurate competitive landscape analysis</li>
                  <li>• Better trademark conflict detection</li>
                  <li>• Relevant market positioning insights</li>
                </ul>
              </div>
            </div>
          );
        }
        return null;

      default:
        return null;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════════════
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-fuchsia-50/20 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <motion.div 
            className="inline-flex items-center gap-2 text-2xl font-black"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
              RIGHTNAME
            </span>
            <span className="text-slate-400">.AI</span>
          </motion.div>
        </div>

        {/* Progress Bar */}
        <div className="w-full mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-500">Step {currentStep} of {totalSteps}</span>
            <span className="text-sm font-medium text-violet-600">{Math.round((currentStep / totalSteps) * 100)}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
          
          {/* Step indicators */}
          <div className="flex justify-between mt-4">
            {(brandStatus === 'existing' ? STEP_LABELS_EXISTING : STEP_LABELS_NEW).map((label, idx) => (
              <div key={idx} className="flex flex-col items-center">
                <motion.div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    idx + 1 < currentStep 
                      ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white' 
                      : idx + 1 === currentStep
                        ? 'bg-violet-100 text-violet-600 ring-2 ring-violet-500'
                        : 'bg-slate-100 text-slate-400'
                  }`}
                  whileHover={{ scale: 1.1 }}
                >
                  {idx + 1 < currentStep ? <Check className="w-4 h-4" /> : idx + 1}
                </motion.div>
                <span className={`text-xs mt-1 ${idx + 1 === currentStep ? 'text-violet-600 font-medium' : 'text-slate-400'}`}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <motion.div 
          className="bg-white rounded-3xl shadow-xl shadow-violet-500/10 p-8 border border-slate-100"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
            <Button
              variant="ghost"
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`flex items-center gap-2 ${currentStep === 1 ? 'invisible' : ''}`}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>

            <div className="flex items-center gap-3">
              {currentStep === totalSteps && (
                <Button
                  data-testid="skip-generate-btn"
                  variant="outline"
                  onClick={handleSubmit}
                  disabled={isProcessing}
                  className="flex items-center gap-2"
                >
                  Skip & Generate
                </Button>
              )}
              
              {currentStep < totalSteps ? (
                <Button
                  data-testid="continue-btn"
                  onClick={nextStep}
                  disabled={!isStepValid}
                  className="flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white px-6 py-2 rounded-xl font-semibold"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  data-testid="generate-report-btn"
                  onClick={handleSubmit}
                  disabled={isProcessing}
                  className="flex items-center gap-2 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-orange-500 hover:from-violet-700 hover:via-fuchsia-700 hover:to-orange-600 text-white px-8 py-3 rounded-xl font-bold shadow-lg shadow-violet-500/30"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate Report
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <p className="text-center text-slate-400 text-sm mt-6">
          Powered by AI • Trusted by 10,000+ founders
        </p>
      </div>
    </div>
  );
};

export default BrandWizard;
